import axios, {
    AxiosError,
    InternalAxiosRequestConfig,
    AxiosResponse,
} from "axios";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { RequestWithIdempotencyKeyDto } from "@common/dto/common/request-with-idempotency-key.dto";
import { SERVICE_PREFIX, VERSION_HEADER } from "@common/constants";
import { scopeProxy } from "@state/scopeContext.lib";
import { clearUser } from "@helpers/auth";
import { newVersionAvailableProxy } from "@components/newVersion/NewVersionAvailable.lib";
import {
    isToastActive,
    showErrorToast,
} from "@design-system/ToastMessages/useToaster";
import { VIEW_ONLY_WRITE_REFUSAL_MESSAGE } from "@common/userWorkspaceRoles";
import { clearAccessToken, getAccessToken } from "./auth0";
import { VIEW_ONLY_TOAST_KEY } from "./viewOnlyRefusal";

export const SUPPRESS_ERROR_TOAST_HEADER = "x-suppress-error-toast";

const zodResultSchema = z
    .object({ issues: z.array(z.object({ message: z.string() })) })
    .nullable()
    .optional();

// ts-rest rejects an invalid request with a 400 whose body carries Zod results
// instead of a `message`, which would otherwise fail silently.
const requestValidationBodySchema = z.object({
    bodyResult: zodResultSchema,
    queryResult: zodResultSchema,
    paramsResult: zodResultSchema,
    headersResult: zodResultSchema,
});

export function getRequestValidationMessage(
    data: AxiosResponse["data"],
): string | undefined {
    const parsed = requestValidationBodySchema.safeParse(data);
    if (!parsed.success) {
        return undefined;
    }
    const { bodyResult, queryResult, paramsResult, headersResult } =
        parsed.data;
    return [bodyResult, queryResult, paramsResult, headersResult]
        .flatMap((result) => result?.issues ?? [])
        .map((issue) => issue.message)
        .find((message) => message.length > 0);
}

const client = axios.create({
    baseURL: `/${SERVICE_PREFIX.MAIN_APP}`,
});

client.interceptors.response.use(
    async (response: AxiosResponse<any>) => response,
    (error: AxiosError<any>) => {
        if (
            (!error.response || error.response?.status >= 500) &&
            isInitialRequestWithIdempotencyKey(error.config)
        ) {
            const data: RequestWithIdempotencyKeyDto = JSON.parse(
                error.config?.data,
            );
            data.idempotencyKeyRetry = true;

            return client.request({
                ...error.config,
                data: JSON.stringify(data),
            });
        }

        if (error.response?.status === 401) {
            clearAccessToken();
            clearUser();

            throw error;
        }

        const shouldSuppressErrorToast = (() => {
            const requestHeaders = error.config?.headers;
            if (!requestHeaders) {
                return false;
            }

            if (
                typeof requestHeaders.get === "function" &&
                requestHeaders.get(SUPPRESS_ERROR_TOAST_HEADER) === "true"
            ) {
                return true;
            }

            const headerValue = (
                requestHeaders as unknown as Record<string, string | undefined>
            )[SUPPRESS_ERROR_TOAST_HEADER];
            return headerValue === "true";
        })();

        const traceIdHeader = error.response?.headers?.["x-trace-id"];
        const traceId =
            (typeof traceIdHeader === "string" ? traceIdHeader : undefined) ??
            error.response?.data?.traceId ??
            undefined;

        if (traceId) {
            Sentry.getCurrentScope().setTag("backend_trace_id", traceId);
        }

        const message =
            error.response?.data?.message ??
            getRequestValidationMessage(error.response?.data);
        if (message && !shouldSuppressErrorToast) {
            const joinedMessage = Array.isArray(message)
                ? message.join("\n")
                : message;
            const notificationMessage =
                typeof joinedMessage === "string"
                    ? joinedMessage
                    : "Unknown error";

            // The view-only refusal shares one toast slot so a bulk fan-out of
            // requests surfaces the sentence once, and a click-time refusal
            // toasted under the same key never stacks with the 403.
            const isViewOnlyRefusal =
                notificationMessage === VIEW_ONLY_WRITE_REFUSAL_MESSAGE;

            if (isViewOnlyRefusal && isToastActive(VIEW_ONLY_TOAST_KEY)) {
                throw error;
            }

            showErrorToast(
                notificationMessage,
                isViewOnlyRefusal ? VIEW_ONLY_TOAST_KEY : undefined,
                { traceId: isViewOnlyRefusal ? undefined : traceId },
            );
        }

        throw error;
    },
);

client.interceptors.response.use((response: AxiosResponse<unknown>) => {
    const traceIdHeader = response.headers["x-trace-id"];
    const traceId =
        typeof traceIdHeader === "string" ? traceIdHeader : undefined;
    if (traceId) {
        Sentry.getCurrentScope().setTag("backend_trace_id", traceId);
    }
    return response;
});

let lastSeenVersion: string | null = null;
client.interceptors.response.use(async (response: AxiosResponse<any>) => {
    const backendVersion = response.headers[VERSION_HEADER];

    if (!backendVersion) {
        return response;
    }

    if (!lastSeenVersion) {
        lastSeenVersion = backendVersion;
    } else if (lastSeenVersion !== backendVersion) {
        newVersionAvailableProxy.notifyNewVersionAvailable();
    }

    return response;
});

client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (config.headers?.authorization) {
        return config;
    }
    try {
        const accessToken = await getAccessToken();

        if (accessToken) {
            config.headers = Object.assign(config.headers ?? {}, {
                authorization: `Bearer ${accessToken}`,
            });
        }

        return config;
    } catch {
        clearAccessToken();
        clearUser();
        scopeProxy.clear();

        throw new axios.Cancel(`Can't get access token`);
    }
});

function isInitialRequestWithIdempotencyKey(
    config: InternalAxiosRequestConfig | undefined,
): boolean {
    if (config) {
        try {
            const data: Partial<RequestWithIdempotencyKeyDto> = JSON.parse(
                config.data,
            );

            return Boolean(data.idempotencyKey && !data.idempotencyKeyRetry);
        } catch {
            return false;
        }
    } else {
        return false;
    }
}

export const backendClient = client;

export function unwrapResponse<T = any>(response: AxiosResponse<T>): T {
    return response?.data;
}
